import React, { FC } from "react"
import clsx from "clsx"

import { Tooltip } from "components/tooltip/Tooltip"

import { useProps } from "contexts/PropsContext"

import { formatFiatCurrency, formatNumber } from "util/formatter"

import { ReactComponent as AttentionIcon } from "../../assets/icons/attention.svg"

type StatCardProps = {
  title: string
  bgColor?: string
  value: number
  previousValue?: number
  loading?: boolean
  type?: string

  infoContent?: React.ReactNode
}

export const StatCard: FC<StatCardProps> = ({ title, value, loading, previousValue, type, infoContent }) => {
  const { darkMode } = useProps()

  const rawChange =
    previousValue !== undefined && previousValue !== 0
      ? ((value - previousValue) / previousValue) * 100
      : previousValue === value
      ? 0
      : null

  const percentChange = rawChange !== null ? rawChange.toFixed(1) : null
  const numericChange = Number(percentChange)

  const isUp = numericChange > 0
  const isDown = numericChange < 0

  const colorHex = isUp ? "#4ade80" : isDown ? "#f87171" : "#9ca3af"

  return (
    <div
      className={clsx(
        "flex flex-col min-w-[200px] justify-between h-full px-6 py-5 rounded-3xl border shadow-[0px_1px_12px_0px_rgba(0,0,0,0.04)]",
        darkMode
          ? "border-[rgba(255,255,255,0.1)] bg-[rgba(255,255,255,0.04)] text-white backdrop-blur-md"
          : "border-[rgba(0,0,0,0.05)] bg-white text-[rgba(6,7,21,1)] backdrop-blur-[9px] shadow-[0px_3px_6px_0px_rgba(192,192,192,0.6)]"
      )}
    >
      <div className="flex flex-col gap-[6px]">
        <div className="flex gap-4">
          <h3 className={clsx("font-medium text-[16px]", darkMode ? "text-[rgba(255,255,255,0.46)]" : "text-[rgba(6,7,21,1)]")}>{title}</h3>
          <div className="flex items-center">
            {infoContent ? (
              <div className="relative">
                <Tooltip content={infoContent} side="left">
                  <AttentionIcon className="cursor-pointer" />
                </Tooltip>
              </div>
            ) : null}
          </div>
        </div>

        <div
          className={clsx("flex justify-between items-center font-medium text-[24px]", {
            "loading loading-spinner loading-xs inline-flex": loading
          })}
        >
          {!loading ? (type === "currency" ? formatFiatCurrency(value, "$0") : formatNumber(value, "0")) : ""}
        </div>
      </div>
      {!loading && percentChange !== null && (
        <div className="flex items-end text-sm leading-none" style={{ color: colorHex }}>
          {isUp && <span>▲</span>}
          {isDown && <span>▼</span>}
          <span>
            {numericChange > 0 ? "+" : ""}
            {percentChange}%
          </span>
        </div>
      )}
    </div>
  )
}
